import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import "./Breadcrumbs.css";

interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface BreadcrumbsProps {
  currentLabel?: string;
  variant?: "hero" | "standard";
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  currentLabel,
  variant = "hero",
}) => {
  const location = useLocation();
  const { t } = useLanguage();

  const items = useMemo(() => {
    const pathname = location.pathname.replace(/\/+$/, "") || "/";

    if (pathname === "/" || pathname === "/login") {
      return [] as BreadcrumbItem[];
    }

    const result: BreadcrumbItem[] = [
      {
        label: t("navbar.home"),
        to: "/",
      },
    ];

    if (pathname === "/gallery") {
      result.push({ label: t("breadcrumbs.gallery") });
      return result;
    }

    if (pathname === "/catalog") {
      const params = new URLSearchParams(location.search);
      let category = params.get("category");
      const duration = params.get("duration");

      if (duration === "1") {
        category = "day_trip";
      } else if (duration === "2+") {
        category = "multi_day";
      }

      if (!category || category === "all") {
        result.push({ label: t("breadcrumbs.catalog") });
        return result;
      }

      const categoryMap: Record<string, string> = {
        day_trip: t("categories.singleDay"),
        multi_day: t("categories.multiDay"),
        cruise: t("categories.cruise"),
        concerts: t("categories.concerts"),
        seaside: t("categories.seaside"),
        special: t("categories.cigliaTours"),
      };

      result.push({ label: t("breadcrumbs.catalog"), to: "/catalog" });
      result.push({ label: categoryMap[category] || category });

      return result;
    }

    if (pathname.startsWith("/travel/")) {
      result.push({ label: t("breadcrumbs.catalog"), to: "/catalog" });
      result.push({ label: currentLabel || t("breadcrumbs.travelDetails") });
      return result;
    }

    if (pathname === "/about") {
      result.push({ label: t("breadcrumbs.about") });
      return result;
    }

    if (pathname === "/contact") {
      result.push({ label: t("breadcrumbs.contact") });
      return result;
    }

    if (pathname === "/join-club") {
      result.push({ label: t("breadcrumbs.joinClub") });
      return result;
    }

    if (pathname === "/admin") {
      result.push({ label: t("breadcrumbs.admin") });
      return result;
    }

    if (pathname.startsWith("/admin/")) {
      result.push({ label: t("breadcrumbs.admin"), to: "/admin" });

      if (pathname === "/admin/add-travel") {
        result.push({ label: t("breadcrumbs.addTravel") });
        return result;
      }

      if (pathname.startsWith("/admin/edit-travel/")) {
        result.push({ label: t("breadcrumbs.editTravel") });
        return result;
      }

      if (pathname === "/admin/users") {
        result.push({ label: t("breadcrumbs.users") });
        return result;
      }

      if (pathname === "/admin/settings") {
        result.push({ label: t("breadcrumbs.settings") });
        return result;
      }
    }

    return result;
  }, [currentLabel, location.pathname, location.search, t]);

  if (items.length < 2) {
    return null;
  }

  return (
    <nav
      className={`breadcrumbs breadcrumbs--${variant}`}
      aria-label="Breadcrumb"
    >
      <ol className="breadcrumbs-list">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <li
              className={`breadcrumbs-item ${isLast ? "is-last" : ""}`}
              key={`${item.label}-${index}`}
            >
              {isLast || !item.to ? (
                <span className="breadcrumbs-current" aria-current="page">
                  {item.label}
                </span>
              ) : (
                <Link className="breadcrumbs-link" to={item.to}>
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
